import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams, Link } from "react-router-dom";
import { toast } from "sonner";
import api from "@/lib/api";
import { ArrowLeft, Plus, Trash2, Save, Loader2 } from "lucide-react";
import BudgetAiChat from "@/components/BudgetAiChat";

const TAX_OPTIONS = [
  { id: "none", label: "Sin impuesto", rate: 0 },
  { id: "iva", label: "IVA", rate: 21 },
  { id: "igic", label: "IGIC", rate: 7 },
];

const emptyItem = { concept: "", quantity: 1, unit: "ud", unit_price: 0 };

export default function BudgetEdit() {
  const { id } = useParams();
  const nav = useNavigate();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [b, setB] = useState(null);
  const [form, setForm] = useState({
    title: "",
    summary: "",
    client_name: "",
    client_phone: "",
    client_email: "",
    client_address: "",
    items: [emptyItem],
    terms: "",
    tax_type: "iva",
    validity_days: 30,
    delivery_days: 7,
    project_reference: "",
    warranty: "",
    payment_method: "",
  });

  useEffect(() => {
    api.get(`/budgets/${id}`).then((r) => {
      hydrate(r.data);
      setLoading(false);
    }).catch(() => { toast.error("Presupuesto no encontrado"); nav("/budgets"); });
  }, [id]); // eslint-disable-line

  const hydrate = (d) => {
    setB(d);
    setForm({
      title: d.ai_data?.title || "",
      summary: d.ai_data?.summary || "",
      client_name: d.client_name || "",
      client_phone: d.client_phone || "",
      client_email: d.client_email || "",
      client_address: d.client_address || "",
      items: (d.ai_data?.items || []).map((it) => ({
        concept: it.concept || "",
        quantity: Number(it.quantity) || 0,
        unit: it.unit || "ud",
        unit_price: Number(it.unit_price) || 0,
      })),
      terms: d.ai_data?.terms || "",
      tax_type: d.tax_type || "iva",
      validity_days: d.ai_data?.validity_days || 30,
      delivery_days: d.ai_data?.delivery_days || 7,
      project_reference: d.project_reference || "",
      warranty: d.ai_data?.warranty || "",
      payment_method: d.ai_data?.payment_method || "",
    });
  };

  // Live totals
  const totals = useMemo(() => {
    const subtotal = form.items.reduce((s, it) => s + (Number(it.quantity) || 0) * (Number(it.unit_price) || 0), 0);
    const rate = TAX_OPTIONS.find((t) => t.id === form.tax_type)?.rate || 0;
    const tax = subtotal * rate / 100;
    return { subtotal: Math.round(subtotal * 100) / 100, tax: Math.round(tax * 100) / 100, total: Math.round((subtotal + tax) * 100) / 100, rate };
  }, [form.items, form.tax_type]);

  const setField = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const setItem = (i, k, v) => {
    const items = [...form.items];
    items[i] = { ...items[i], [k]: v };
    setForm({ ...form, items });
  };
  const addItem = () => setForm({ ...form, items: [...form.items, { ...emptyItem }] });
  const removeItem = (i) => setForm({ ...form, items: form.items.filter((_, idx) => idx !== i) });

  const save = async (e) => {
    e.preventDefault();
    if (!form.title) return toast.error("El título es obligatorio");
    if (!form.client_name) return toast.error("Indica el nombre del cliente");
    if (form.items.length === 0) return toast.error("Añade al menos una línea");
    setSaving(true);
    try {
      const payload = {
        title: form.title,
        summary: form.summary,
        client_name: form.client_name,
        client_phone: form.client_phone,
        client_email: form.client_email,
        client_address: form.client_address,
        items: form.items.map((it) => ({
          concept: it.concept,
          quantity: Number(it.quantity) || 0,
          unit: it.unit || "ud",
          unit_price: Number(it.unit_price) || 0,
        })),
        terms: form.terms,
        tax_type: form.tax_type,
        validity_days: Number(form.validity_days) || 30,
        delivery_days: Number(form.delivery_days) || 7,
      };
      await api.put(`/budgets/${id}`, payload);
      toast.success("Presupuesto actualizado");
      nav(`/budgets/${id}`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Error al guardar");
    } finally { setSaving(false); }
  };

  if (loading) return <div className="text-slate-500">Cargando…</div>;

  return (
    <div data-testid="budget-edit-page">
      <Link to={`/budgets/${id}`} className="inline-flex items-center gap-2 text-sm text-slate-500 hover:text-slate-900"><ArrowLeft className="w-4 h-4" /> Volver</Link>
      <div className="mt-3 flex items-end justify-between flex-wrap gap-4">
        <div>
          <div className="kbar">{b?.number}</div>
          <h1 className="font-display font-extrabold text-2xl sm:text-4xl tracking-tight mt-2">Editar presupuesto</h1>
          {b?.updated_at && <p className="text-xs text-slate-400 mt-1">Última modificación: {new Date(b.updated_at).toLocaleString("es-ES")}</p>}
        </div>
      </div>

      <form onSubmit={save} className="mt-6 grid grid-cols-1 lg:grid-cols-3 gap-px bg-slate-200 border border-slate-200">
        {/* Left main */}
        <div className="bg-white p-6 lg:col-span-2 space-y-6">
          <div>
            <label className="kbar block">Título</label>
            <input data-testid="edit-title" required value={form.title} onChange={(e) => setField("title", e.target.value)} className="w-full h-12 mt-2 px-3 border border-slate-200 focus:border-orange-600 outline-none" />
          </div>

          <div>
            <label className="kbar block">Referencia / Obra</label>
            <input
              data-testid="edit-project-ref"
              value={form.project_reference}
              onChange={(e) => setField("project_reference", e.target.value)}
              placeholder="ej. Reforma cocina C/Mayor 32 · Fase 1"
              className="w-full h-12 mt-2 px-3 border border-slate-200 focus:border-orange-600 outline-none"
            />
            <p className="text-xs text-slate-500 mt-1">Útil para distinguir varios presupuestos del mismo cliente.</p>
          </div>

          <div>
            <label className="kbar block">Resumen</label>
            <textarea data-testid="edit-summary" rows={2} value={form.summary} onChange={(e) => setField("summary", e.target.value)} className="w-full mt-2 p-3 border border-slate-200 focus:border-orange-600 outline-none resize-none" />
          </div>

          {/* Items */}
          <div>
            <div className="flex items-center justify-between mb-3">
              <label className="kbar">Líneas de concepto</label>
              <button type="button" onClick={addItem} data-testid="add-item" className="h-9 px-3 border border-slate-200 hover:border-slate-300 inline-flex items-center gap-1 text-sm font-semibold"><Plus className="w-4 h-4" /> Añadir línea</button>
            </div>
            <div className="border border-slate-200 overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-slate-50 border-b border-slate-200">
                  <tr>
                    <Th className="w-1/2">Concepto</Th>
                    <Th>Cantidad</Th>
                    <Th>Ud.</Th>
                    <Th>Precio</Th>
                    <Th>Total</Th>
                    <Th></Th>
                  </tr>
                </thead>
                <tbody>
                  {form.items.map((it, i) => {
                    const lt = (Number(it.quantity) || 0) * (Number(it.unit_price) || 0);
                    return (
                      <tr key={i} className="border-b border-slate-100 last:border-b-0" data-testid={`item-row-${i}`}>
                        <td className="p-1">
                          <input data-testid={`item-concept-${i}`} required value={it.concept} onChange={(e) => setItem(i, "concept", e.target.value)} className="w-full h-10 px-2 border border-slate-200 focus:border-orange-600 outline-none text-sm" />
                        </td>
                        <td className="p-1">
                          <input data-testid={`item-qty-${i}`} type="number" min="0" step="0.01" value={it.quantity} onChange={(e) => setItem(i, "quantity", e.target.value)} className="w-full h-10 px-2 border border-slate-200 focus:border-orange-600 outline-none text-sm tabular-nums" />
                        </td>
                        <td className="p-1">
                          <input data-testid={`item-unit-${i}`} value={it.unit} onChange={(e) => setItem(i, "unit", e.target.value)} className="w-full h-10 px-2 border border-slate-200 focus:border-orange-600 outline-none text-sm" />
                        </td>
                        <td className="p-1">
                          <input data-testid={`item-price-${i}`} type="number" min="0" step="0.01" value={it.unit_price} onChange={(e) => setItem(i, "unit_price", e.target.value)} className="w-full h-10 px-2 border border-slate-200 focus:border-orange-600 outline-none text-sm tabular-nums" />
                        </td>
                        <td className="p-3 text-right tabular-nums font-semibold whitespace-nowrap">{lt.toFixed(2)} €</td>
                        <td className="p-1 text-right">
                          <button type="button" onClick={() => removeItem(i)} data-testid={`item-del-${i}`} disabled={form.items.length === 1} className="text-slate-400 hover:text-red-600 disabled:opacity-30 p-2"><Trash2 className="w-4 h-4" /></button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>

          <div>
            <label className="kbar block">Notas / Condiciones</label>
            <textarea data-testid="edit-terms" rows={4} value={form.terms} onChange={(e) => setField("terms", e.target.value)} className="w-full mt-2 p-3 border border-slate-200 focus:border-orange-600 outline-none resize-none" />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="kbar block">Validez (días)</label>
              <input data-testid="edit-validity" type="number" min="1" value={form.validity_days} onChange={(e) => setField("validity_days", e.target.value)} className="w-full h-12 mt-2 px-3 border border-slate-200 focus:border-orange-600 outline-none" />
            </div>
            <div>
              <label className="kbar block">Plazo de entrega (días)</label>
              <input data-testid="edit-delivery" type="number" min="1" value={form.delivery_days} onChange={(e) => setField("delivery_days", e.target.value)} className="w-full h-12 mt-2 px-3 border border-slate-200 focus:border-orange-600 outline-none" />
            </div>
          </div>
        </div>

        {/* Right sidebar */}
        <div className="bg-white p-6 space-y-6">
          <div>
            <div className="kbar mb-2">Cliente</div>
            <input data-testid="edit-client-name" required value={form.client_name} onChange={(e) => setField("client_name", e.target.value)} placeholder="Nombre" className="w-full h-11 px-3 border border-slate-200 focus:border-orange-600 outline-none" />
            <input data-testid="edit-client-phone" value={form.client_phone} onChange={(e) => setField("client_phone", e.target.value)} placeholder="Teléfono" className="w-full h-11 mt-2 px-3 border border-slate-200 focus:border-orange-600 outline-none" />
            <input data-testid="edit-client-email" type="email" value={form.client_email} onChange={(e) => setField("client_email", e.target.value)} placeholder="Email" className="w-full h-11 mt-2 px-3 border border-slate-200 focus:border-orange-600 outline-none" />
            <input data-testid="edit-client-address" value={form.client_address} onChange={(e) => setField("client_address", e.target.value)} placeholder="Dirección" className="w-full h-11 mt-2 px-3 border border-slate-200 focus:border-orange-600 outline-none" />
          </div>

          <div>
            <div className="kbar mb-2">Gravamen</div>
            <div className="grid grid-cols-3 gap-2">
              {TAX_OPTIONS.map((t) => (
                <button type="button" key={t.id} data-testid={`edit-tax-${t.id}`} onClick={() => setField("tax_type", t.id)} className={`p-2 text-center border transition-colors ${form.tax_type === t.id ? "bg-slate-900 text-white border-slate-900" : "bg-white border-slate-200 hover:border-slate-300"}`}>
                  <div className="font-bold text-sm">{t.label}</div>
                  <div className={`text-[10px] ${form.tax_type === t.id ? "text-slate-300" : "text-slate-500"}`}>{t.rate}%</div>
                </button>
              ))}
            </div>
          </div>

          <div className="border-t border-slate-200 pt-4">
            <div className="kbar mb-3">Totales</div>
            <dl className="text-sm space-y-1">
              <Row label="Subtotal" v={`${totals.subtotal.toFixed(2)} €`} testid="totals-subtotal" />
              <Row label={totals.rate === 0 ? "Sin impuesto" : `${TAX_OPTIONS.find((t) => t.id === form.tax_type)?.label} (${totals.rate}%)`} v={`${totals.tax.toFixed(2)} €`} testid="totals-tax" />
              <Row label="TOTAL" v={`${totals.total.toFixed(2)} €`} bold testid="totals-total" />
            </dl>
          </div>

          <div className="space-y-2 pt-2">
            <button type="submit" disabled={saving} data-testid="edit-save" className="btn-brand h-12 w-full inline-flex items-center justify-center gap-2 disabled:opacity-50">
              {saving ? <><Loader2 className="w-4 h-4 animate-spin" /> Guardando…</> : <><Save className="w-4 h-4" /> Guardar cambios</>}
            </button>
            <Link to={`/budgets/${id}`} className="block text-center h-11 leading-[2.75rem] border border-slate-200 hover:border-slate-300 text-sm font-semibold" data-testid="edit-cancel">Cancelar</Link>
          </div>
        </div>
      </form>

      {/* AI chat panel */}
      <div className="mt-6 grid grid-cols-1 lg:grid-cols-3 gap-px bg-slate-200 border border-slate-200">
        <div className="lg:col-span-3">
          <BudgetAiChat
            budgetId={id}
            history={b?.refine_history || []}
            status={b?.status}
            onApplied={(updated) => hydrate(updated)}
          />
        </div>
      </div>
    </div>
  );
}

const Th = ({ children, className = "" }) => <th className={`text-left px-3 py-2 kbar ${className}`}>{children}</th>;

function Row({ label, v, bold, testid }) {
  return (
    <div className="flex justify-between items-baseline" data-testid={testid}>
      <span className="text-slate-500">{label}</span>
      <span className={bold ? "font-bold text-slate-900 text-lg tabular-nums" : "text-slate-700 tabular-nums"}>{v}</span>
    </div>
  );
}
